import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

import {
  Experiment,
  ExperimentClient,
  ExperimentUser,
  Variant,
  Variants,
} from '@amplitude/experiment-react-native-client';
import { LogLevel } from 'features/observability/datadog/types';
import { syncDatadogExperimentVariantContext } from 'features/observability/experiment-variants';

import { minimalCartExposureSessionLatch } from 'experiments/minimal-cart-exposure-sessions';
import { orderPendingProcessModalExposureSessionLatch } from 'experiments/order-pending-process-modal-exposure-sessions';
import { EventTypes, logEvent } from 'state/amplitude';
import {
  getPendingAmplitudeIdentityUpdate,
  isAmplitudeIdentityUpdateOptOutResult,
  isSuccessfulAmplitudeIdentityUpdateResult,
  subscribeToAmplitudeIdentityUpdates,
} from 'state/amplitude/identity-update';
import { appVersion } from 'utils/app-version-info';
import { getDeviceIdAsync } from 'utils/device-id';
import { region as buildRegion, getApiKey, minDevLogLevel } from 'utils/environment';
import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';
import logger from 'utils/logger';
import noop from 'utils/noop';
import { TraceName, startTrace } from 'utils/performance/trace';

import {
  confirmingHttpClient,
  getVariantsFetchOutcomeSince,
  markVariantsFetchObservation,
} from './confirming-http-client';
import { RunningExperiments } from './running-experiments';
import { ExposureOptions, IExperimentsContext, VariantOptions } from './types';

export { RunningExperiments } from './running-experiments';

/** Identifies which identity transition an assignment refresh belongs to for structured logs. */
type ExperimentFetchOperation = 'startup' | 'login' | 'logout' | 'identify';

const ASSIGNMENT_LOADING_TIMEOUT_MS = 3_000;
const LOGIN_IDENTIFY_WAIT_MS = 3_000;
const EXPERIMENT_FETCH_MAX_ATTEMPTS = 3;
const EXPERIMENT_FETCH_RETRY_DELAY_MS = 500;
const STARTUP_BACKGROUND_RETRY_DELAY_MS = 2_000;
// Bounds how many background recoveries a terminally-failed identity refresh schedules before it
// waits for an external identity signal, so a persistent outage cannot retry forever.
const MAX_BACKGROUND_RECOVERY_ATTEMPTS = 3;
// Bounds how long a single fetch may hold the serialized queue so a hanging request cannot block
// subsequent identity refreshes indefinitely.
const EXPERIMENT_FETCH_TIMEOUT_MS = 5_000;

const Context = React.createContext<IExperimentsContext>({
  isLoading: false,
  isAssignmentLoading: false,
  assignmentLoadingDeadline: 0,
  loadingCycle: 0,
  exposureRevision: 0,
  hasAssignmentFetchFailed: false,
  getVariantValue: (_flag: string | RunningExperiments) => undefined,
  logExposure: (_flag: string | RunningExperiments, _options?: ExposureOptions) => false,
  handleLogin: noop,
  handleLogout: noop,
  allVariants: {} as Variants,
  variant: (_flag: RunningExperiments, _expectedVariant: string, _options?: VariantOptions) =>
    false,
});

export const useExperiments = () => useContext(Context);

export const experiments = Experiment.initializeWithAmplitudeAnalytics(
  getApiKey({ key: 'amplitudeExperiments' }),
  {
    debug: __DEV__ && minDevLogLevel() <= LogLevel.debug,
    automaticExposureTracking: false, // we track this manually for many experiments
    // The provider owns identity-triggered fetches through its serialized queue so a stale
    // SDK-owned background fetch cannot publish an obsolete identity's assignment.
    automaticFetchOnAmplitudeIdentityChange: false,
    // The provider owns retries through its serialized queue and generation guards, so the SDK's own
    // background retry is disabled to avoid an out-of-band request repopulating the cache.
    retryFetchOnFailure: false,
    // ExperimentClient.fetch() swallows request/timeout errors and always resolves, hiding network
    // failures behind an empty assignment. This transport mirrors the SDK default behavior but
    // records each request outcome so the provider can tell a failed refresh from an empty one.
    httpClient: confirmingHttpClient,
    // Bound each request so a hanging fetch cannot hold the serialized queue indefinitely.
    fetchTimeoutMillis: EXPERIMENT_FETCH_TIMEOUT_MS,
  }
);

let lastFetchedUserId: string | undefined;
// Governs which in-flight fetch may publish. A newer identity refresh increments it so an older
// evaluation cannot overwrite the current identity's assignment.
let experimentFetchGeneration = 0;
// Serializes assignment evaluations so queued cache mutations run in identity order.
let experimentFetchQueue: Promise<void> = Promise.resolve();
let experimentsInitializationPromise: Promise<ExperimentClient> | null = null;
let startupFetchGeneration = 0;
// Number of in-flight Identify-triggered property refreshes. They do not advance the generation, so
// a remount checks this to recover a refresh that is updating the current (confirmed) generation.
let pendingIdentityPropertyRefreshCount = 0;
// The newest generation whose assignment has been confirmed (published). A remount only needs to
// re-evaluate when this trails the current generation, i.e. a refresh was still in flight.
let lastConfirmedFetchGeneration = 0;
// Background recoveries scheduled since the last user-initiated identity change. Reset when a new
// identity transition starts so each identity gets a fresh recovery budget.
let backgroundRecoveryAttempts = 0;
let hasCompletedExperimentsInitialization = false;

export const experimentsCacheReadyPromise =
  typeof experiments.cacheReady === 'function' ? experiments.cacheReady() : Promise.resolve();
const experimentsCacheSettledPromise = experimentsCacheReadyPromise.catch(error => {
  logger.error({ error, message: 'Amplitude experiments cache init failure' });
});

const delay = (durationMs: number): Promise<void> =>
  new Promise(resolve => setTimeout(resolve, durationMs));

export const initializeExperiments = (): Promise<ExperimentClient> => {
  if (!experimentsInitializationPromise) {
    const initialUserId = syncStorage.getString(SyncStorageKey.USER_ID);
    lastFetchedUserId = initialUserId;
    // Reserve the startup generation before waiting for cache readiness so a newer identity
    // refresh that starts during the deferred wait keeps ordering ahead of startup.
    startupFetchGeneration = ++experimentFetchGeneration;
    // Startup runs a single foreground attempt so the provider settles shared loading right after it,
    // instead of blocking unrelated experiment consumers through multiple request timeouts. A
    // failure is recovered in the background by the provider's terminal-failure recovery.
    const startupPromise = experimentsCacheSettledPromise
      .then(() => enqueueFetchExperimentsForUser(initialUserId, startupFetchGeneration))
      .then(client => {
        recordConfirmedFetch(startupFetchGeneration);
        return client;
      });
    // A terminal startup failure must not be memoized, so a later caller or a provider remount can
    // retry startup instead of replaying the rejected promise forever.
    void startupPromise.catch(() => {
      if (experimentsInitializationPromise === startupPromise) {
        experimentsInitializationPromise = null;
      }
    });
    experimentsInitializationPromise = startupPromise;
  }

  return experimentsInitializationPromise;
};

async function fetchExperimentsForUser(userId?: string): Promise<ExperimentClient> {
  const trace = startTrace(TraceName.APP_START_INIT_EXPERIMENTS);
  const deviceId = await getDeviceIdAsync();
  const user: ExperimentUser = {
    country: buildRegion,
    device_id: deviceId.toLowerCase(),
    region: buildRegion,
    user_properties: {
      region: buildRegion,
    },
    version: appVersion,
  };

  if (userId) {
    user.user_id = userId;
  }

  const fetchObservationToken = markVariantsFetchObservation();
  const result = await experiments.fetch(user);

  trace?.stop();

  // ExperimentClient.fetch() resolves even when the request fails, so consult the confirming
  // transport and require a positively observed successful variants request. A 'failure' is a
  // non-2xx or network error; a 'none' means fetch() errored before issuing the request at all (an
  // empty API key, or an identity-readiness timeout), which the SDK also swallows. Treating either
  // as failure runs the provider retry lifecycle instead of confirming a cached or empty assignment.
  if (getVariantsFetchOutcomeSince(fetchObservationToken) !== 'success') {
    throw new Error('Amplitude experiments assignment fetch failed');
  }

  return result;
}

/**
 * Runs an assignment fetch serialized on the shared queue so it cannot overlap an identity
 * transition's fetch. Skips the fetch when a newer identity superseded this evaluation before it
 * reached the front of the queue, so a stale identity's fetch cannot overwrite the current SDK
 * cache (the caller's generation check drops the returned client without publishing it).
 */
function enqueueFetchExperimentsForUser(
  userId: string | undefined,
  generation: number
): Promise<ExperimentClient> {
  const fetchResult = experimentFetchQueue.then(async () => {
    if (generation !== experimentFetchGeneration) {
      return experiments;
    }
    const client = await fetchExperimentsForUser(userId);
    if (generation !== experimentFetchGeneration) {
      // A newer identity superseded this fetch while it was in flight. Clear the assignments it
      // persisted so a later read (e.g. a provider remount) cannot surface the obsolete identity's
      // variants; the caller's generation check already drops the returned client.
      experiments.clear();
    }
    return client;
  });
  experimentFetchQueue = fetchResult.then(noop, noop);
  return fetchResult;
}

/** Records that a fetch published at least once and confirms its generation if still current. */
function recordConfirmedFetch(generation: number): void {
  // Only a current fetch actually published; a superseded startup that never fetched must not mark
  // initialization complete or advance the confirmed generation.
  if (generation !== experimentFetchGeneration) {
    return;
  }
  hasCompletedExperimentsInitialization = true;
  lastConfirmedFetchGeneration = generation;
}

/** True when two variants describe the same rendered assignment identity. */
const hasSameAssignmentIdentity = (a?: Variant, b?: Variant): boolean =>
  a?.expKey === b?.expKey && a?.key === b?.key && a?.value === b?.value;

/** Builds the append-only provider dedupe key from a committed assignment and caller dedupe key. */
const getExposureDedupeKey = (
  flagKey: string | RunningExperiments,
  variant: Variant,
  dedupeKey: string
): string =>
  JSON.stringify([flagKey, variant.expKey, variant.key ?? variant.value, variant.value, dedupeKey]);

export const ExperimentsProvider = ({ children }: { children: React.ReactNode }) => {
  const isMountedRef = useRef(true);
  const flagExposuresHandled = useRef(new Map<string, boolean>());
  // The latest fetched assignments, updated synchronously so exposure can borrow refreshed
  // metadata for an assignment identity that still matches the consumer's committed render.
  const exposureVariantsRef = useRef<Variants>({});
  // Mirrors isAssignmentLoading for the synchronous exposure gate, and the revision the current
  // identity generation belongs to so exposure callbacks from a superseded render can be rejected.
  const isAssignmentLoadingRef = useRef(true);
  const activeExposureRevisionRef = useRef(0);
  const activeIdentityRef = useRef<{ userId: string | undefined } | null>(null);
  const isIdentityRefreshQueuedRef = useRef(false);

  const [isLoading, setIsLoading] = useState(true);
  const [isAssignmentLoading, setIsAssignmentLoading] = useState(true);
  const [assignmentLoadingDeadline, setAssignmentLoadingDeadline] = useState(
    () => Date.now() + ASSIGNMENT_LOADING_TIMEOUT_MS
  );
  const [loadingCycle, setLoadingCycle] = useState(0);
  const [exposureRevision, setExposureRevision] = useState(0);
  const [hasAssignmentFetchFailed, setHasAssignmentFetchFailed] = useState(false);
  const [variants, setVariants] = useState({} as Variants);

  const updateVariants = useCallback((newVariants: Variants) => {
    exposureVariantsRef.current = newVariants;
    if (!isMountedRef.current) {
      return;
    }
    setVariants(prevVariants => {
      // Only replace this high-fanout context state when a rendered assignment identity changed.
      const keys = new Set([...Object.keys(newVariants), ...Object.keys(prevVariants)]);
      for (const key of keys) {
        const nextVariant = newVariants[key];
        const previousVariant = prevVariants[key];
        const nextExposurePathname =
          typeof nextVariant?.payload?.pathname === 'string'
            ? nextVariant.payload.pathname
            : undefined;
        const previousExposurePathname =
          typeof previousVariant?.payload?.pathname === 'string'
            ? previousVariant.payload.pathname
            : undefined;
        const hasMenuExposurePathnameChanged =
          key === RunningExperiments.OngoingMenuExperiment &&
          nextExposurePathname !== previousExposurePathname;
        if (
          !hasSameAssignmentIdentity(nextVariant, previousVariant) ||
          hasMenuExposurePathnameChanged
        ) {
          return newVariants;
        }
      }
      return prevVariants;
    });
  }, []);

  const settleSharedLoading = useCallback((generation: number) => {
    if (isMountedRef.current && generation === experimentFetchGeneration) {
      setIsLoading(false);
    }
  }, []);

  const settleAssignmentRefresh = useCallback((generation: number, didFail: boolean) => {
    if (generation !== experimentFetchGeneration) {
      return;
    }
    // Resume exposure reporting only once the current identity's assignment is confirmed. On a
    // terminal failure keep it suppressed so a stale previous-identity assignment is not exposed
    // under the new Amplitude identity; the loading state still settles for consumer fallback.
    if (!didFail) {
      isAssignmentLoadingRef.current = false;
    }
    if (activeIdentityRef.current?.userId === lastFetchedUserId) {
      activeIdentityRef.current = null;
    }
    if (isMountedRef.current) {
      setIsLoading(false);
      setIsAssignmentLoading(false);
      setHasAssignmentFetchFailed(didFail);
    }
  }, []);

  const runFetchAttemptRef = useRef<
    (
      userId: string | undefined,
      operation: ExperimentFetchOperation,
      generation: number,
      attempt: number
    ) => void
  >(noop);
  // Set once recoverCurrentIdentityRefresh is defined below, so an Identify refresh (declared first)
  // can route a failure into the shared retry/background-recovery lifecycle.
  const recoverCurrentIdentityRefreshRef = useRef<(operation: ExperimentFetchOperation) => void>(
    noop
  );
  // True when a login proceeded at the Identify deadline with the Identify still pending, so its
  // fetch used properties from before the Identify. A later successful Identify completion must then
  // refresh the assignment even though the login transition is still active.
  const loginNeedsLateIdentifyRefreshRef = useRef(false);

  const runFetchAttempt = useCallback(
    (
      userId: string | undefined,
      operation: ExperimentFetchOperation,
      generation: number,
      attempt: number
    ) => {
      const enqueueFetch = () => {
        experimentFetchQueue = experimentFetchQueue
          .then(async () => {
            await experimentsCacheSettledPromise;
            if (generation !== experimentFetchGeneration) {
              return;
            }
            // Logout clears again at the anonymous fetch boundary in case a hydrating cache
            // restored authenticated assignments after the immediate clear.
            if (userId === undefined) {
              experiments.clear();
            }

            const client = await fetchExperimentsForUser(userId);
            if (generation !== experimentFetchGeneration) {
              // A newer identity superseded this evaluation. Clear the assignments this obsolete
              // fetch persisted so a later read cannot surface them.
              experiments.clear();
              return;
            }
            recordConfirmedFetch(generation);
            const confirmedVariants = client.all();
            updateVariants(confirmedVariants);
            settleAssignmentRefresh(generation, false);
          })
          .catch(error => {
            logger.error({
              error,
              message: 'Amplitude experiments assignment refresh failure',
              operation,
            });
            // Shared loading settles after the first attempt so unrelated experiment consumers are
            // not blocked while a retry runs in the background.
            settleSharedLoading(generation);
            if (
              attempt + 1 < EXPERIMENT_FETCH_MAX_ATTEMPTS &&
              generation === experimentFetchGeneration
            ) {
              void delay(EXPERIMENT_FETCH_RETRY_DELAY_MS).then(() => {
                if (generation === experimentFetchGeneration) {
                  runFetchAttemptRef.current(userId, operation, generation, attempt + 1);
                }
              });
              return;
            }
            settleAssignmentRefresh(generation, true);
            // Terminal failure keeps exposure suppressed. The production login path calls the handler
            // once and schedules no retry, so recover in the background (bounded) instead of waiting
            // for another external identity signal. Re-run the same generation so no new transition
            // resets exposure state; the generation guard drops this if a newer identity superseded.
            if (backgroundRecoveryAttempts < MAX_BACKGROUND_RECOVERY_ATTEMPTS) {
              backgroundRecoveryAttempts += 1;
              void delay(STARTUP_BACKGROUND_RETRY_DELAY_MS).then(() => {
                if (
                  isMountedRef.current &&
                  generation === experimentFetchGeneration &&
                  lastConfirmedFetchGeneration !== generation
                ) {
                  runFetchAttemptRef.current(userId, operation, generation, 0);
                }
              });
            }
          });
      };

      // Wait (bounded) for a pending login Identify BEFORE enqueuing the fetch so the wait does not
      // block other identity transitions already in the serialized queue. Only the first attempt
      // waits; if a newer identity supersedes this one during the wait, the fetch is never enqueued.
      if (userId !== undefined && attempt === 0) {
        // The authenticated user-id notification starts this transition synchronously, before the
        // same login sequence calls updateAmplitudeUserAttributes() to mark the login Identify
        // pending (loginCRM -> updateAmplitudeUserId -> this transition, then
        // updateAmplitudeUserAttributes -> identify(), then handleLogin joins by coalescing). Read
        // the pending Identify on a microtask so that synchronous sequence has finished; otherwise
        // the transition evaluates the authenticated assignment before its properties are applied.
        void Promise.resolve().then(() => {
          if (generation !== experimentFetchGeneration) {
            return;
          }
          const pendingIdentityUpdate = getPendingAmplitudeIdentityUpdate();
          if (pendingIdentityUpdate) {
            // Proceed early only on a favorable Identify (a successful delivery or the documented
            // opt-out). A retryable transport failure keeps the authenticated assignment suppressed
            // until the bounded deadline, so exposure does not resume on properties Amplitude never
            // applied; the deadline still evaluates the explicit user id, and a later successful
            // Identify remains eligible to refresh with the completed properties.
            let hasProceeded = false;
            const proceedOnce = (incorporatedIdentify: boolean): void => {
              if (hasProceeded) {
                return;
              }
              hasProceeded = true;
              if (generation === experimentFetchGeneration) {
                // Proceeding at the deadline runs the fetch while the Identify is still pending, so
                // its properties are stale and a later successful completion must refresh. Proceeding
                // via the Identify incorporates it, so no late refresh is needed. Only record this
                // for the current transition; a superseded one must not set a stale flag.
                loginNeedsLateIdentifyRefreshRef.current = !incorporatedIdentify;
                enqueueFetch();
              }
            };
            void delay(LOGIN_IDENTIFY_WAIT_MS).then(() => proceedOnce(false));
            void pendingIdentityUpdate.then(result => {
              if (
                isSuccessfulAmplitudeIdentityUpdateResult(result) ||
                isAmplitudeIdentityUpdateOptOutResult(result)
              ) {
                proceedOnce(true);
              }
            }, noop);
            return;
          }
          enqueueFetch();
        });
        return;
      }

      enqueueFetch();
    },
    [settleAssignmentRefresh, settleSharedLoading, updateVariants]
  );
  runFetchAttemptRef.current = runFetchAttempt;

  const beginIdentityTransition = useCallback(
    (
      userId: string | undefined,
      operation: ExperimentFetchOperation,
      { resetExposureState }: { resetExposureState: boolean }
    ) => {
      const generation = ++experimentFetchGeneration;
      activeIdentityRef.current = { userId };
      lastFetchedUserId = userId;
      // A new transition supersedes any pending late-Identify refresh from a previous login.
      loginNeedsLateIdentifyRefreshRef.current = false;
      // Suppress exposure until this transition confirms the identity's assignment.
      isAssignmentLoadingRef.current = true;
      if (resetExposureState) {
        // A new identity gets its own exposure evaluation: reset the provider dedupe and the
        // assignment-following session latches, and advance the exposure revision so callbacks
        // captured for the previous identity are rejected.
        flagExposuresHandled.current.clear();
        minimalCartExposureSessionLatch.clear();
        orderPendingProcessModalExposureSessionLatch.clear();
        activeExposureRevisionRef.current += 1;
        // A user-initiated identity change gets a fresh background-recovery budget.
        backgroundRecoveryAttempts = 0;
      }
      if (isMountedRef.current) {
        if (resetExposureState) {
          setExposureRevision(activeExposureRevisionRef.current);
        }
        setIsAssignmentLoading(true);
        setAssignmentLoadingDeadline(Date.now() + ASSIGNMENT_LOADING_TIMEOUT_MS);
        setLoadingCycle(cycle => cycle + 1);
        setHasAssignmentFetchFailed(false);
      }
      if (resetExposureState) {
        // Clear persisted assignments immediately for every identity change. The Experiment SDK
        // resolves fetch() even when the request fails, keeping the prior identity's variants in the
        // cache; clearing first means a failed refresh publishes an empty assignment rather than
        // surfacing the previous identity's assignment under the new identity.
        experiments.clear();
        if (isMountedRef.current) {
          setVariants({} as Variants);
        }
      }

      runFetchAttempt(userId, operation, generation, 0);
    },
    [runFetchAttempt]
  );

  const runIdentityRefresh = useCallback(
    (userId: string | undefined, operation: ExperimentFetchOperation) => {
      const isLogout = userId === undefined;
      // Coalesce a duplicate signal for the same in-flight transition (the identity bridge
      // notification and the authentication handler both fire for one login/logout).
      if (activeIdentityRef.current !== null && activeIdentityRef.current.userId === userId) {
        return;
      }
      // A login for the already-resolved, confirmed identity is a no-op when no transition is in
      // flight. If the previous refresh for this identity failed (still suppressed), let the login
      // retry it. A pending Identify no longer blocks this no-op: the identity listener below
      // already re-evaluates the assignment once that Identify resolves, so we don't need to reset
      // exposure state here too.
      if (
        !isLogout &&
        userId === lastFetchedUserId &&
        activeIdentityRef.current === null &&
        !isAssignmentLoadingRef.current
      ) {
        return;
      }

      beginIdentityTransition(userId, operation, { resetExposureState: true });
    },
    [beginIdentityTransition]
  );

  const refreshCurrentIdentityProperties = useCallback(() => {
    // Coalesce rapid Identify notifications into a single re-evaluation of the current identity.
    if (isIdentityRefreshQueuedRef.current) {
      return;
    }
    isIdentityRefreshQueuedRef.current = true;
    pendingIdentityPropertyRefreshCount += 1;
    const userId = lastFetchedUserId;
    // A completed Identify can change assignments through refreshed user properties without a new
    // identity, so re-evaluate the current identity without disturbing generation or exposure
    // state. Publish only if no identity transition superseded this snapshot.
    const generation = experimentFetchGeneration;
    experimentFetchQueue = experimentFetchQueue.then(async () => {
      // Release the coalescing latch once this refresh starts so a later property change queues.
      isIdentityRefreshQueuedRef.current = false;
      try {
        await experimentsCacheSettledPromise;
        if (generation !== experimentFetchGeneration) {
          return;
        }
        const client = await fetchExperimentsForUser(userId);
        if (generation !== experimentFetchGeneration) {
          // A newer identity superseded this refresh while it was fetching. Clear the assignments it
          // persisted so a later read cannot surface the obsolete identity's variants.
          experiments.clear();
          return;
        }
        recordConfirmedFetch(generation);
        const confirmedVariants = client.all();
        updateVariants(confirmedVariants);
        // With no identity transition in flight, this refresh confirms the current identity, so
        // resume exposure reporting (e.g. after a failed startup that an Identify recovered).
        const confirmsCurrentIdentity = activeIdentityRef.current === null;
        if (confirmsCurrentIdentity) {
          isAssignmentLoadingRef.current = false;
        }
        if (isMountedRef.current) {
          setHasAssignmentFetchFailed(false);
          if (confirmsCurrentIdentity) {
            setIsAssignmentLoading(false);
          }
        }
      } catch (error) {
        logger.error({
          error,
          message: 'Amplitude experiments assignment refresh failure',
          operation: 'identify',
        });
        // The SDK's own retry is disabled, so a transient failure would otherwise retain the previous
        // assignment until the next identity event. Recover the current identity through the shared
        // retry/background-recovery lifecycle, unless a newer transition already superseded this.
        if (generation === experimentFetchGeneration) {
          recoverCurrentIdentityRefreshRef.current('identify');
        }
      } finally {
        pendingIdentityPropertyRefreshCount -= 1;
      }
    });
  }, [updateVariants]);

  const recoverCurrentIdentityRefresh = useCallback(
    (operation: ExperimentFetchOperation) => {
      // Re-evaluate the current identity as a transition without resetting exposure state (same
      // identity), keeping exposure suppressed until this instance confirms the assignment. Used to
      // recover a refresh that was in flight on a previous provider instance, and to retry a failed
      // startup in the background.
      beginIdentityTransition(lastFetchedUserId, operation, { resetExposureState: false });
    },
    [beginIdentityTransition]
  );
  recoverCurrentIdentityRefreshRef.current = recoverCurrentIdentityRefresh;

  const adoptCurrentIdentityAssignment = useCallback(() => {
    // Adopt the SDK's current assignment for a provider instance that did not publish the active
    // identity refresh: a remount after startup completed, or a startup superseded by a queued
    // identity change whose publication went to the now-detached previous instance. Resume exposure
    // when the current identity is confirmed and idle; otherwise recover the in-flight refresh as a
    // transition so this instance publishes it instead of loading indefinitely.
    const currentVariants = experiments.all();
    // A pending Identify property refresh hasn't cleared the cache yet, so the current generation's
    // published assignment can be adopted. Only resuming exposure needs to wait for the refresh,
    // since it may republish a different assignment for the same generation.
    const hasPublishedCurrentGeneration =
      experimentFetchGeneration === lastConfirmedFetchGeneration;
    const hasConfirmedCurrentIdentity =
      hasPublishedCurrentGeneration && pendingIdentityPropertyRefreshCount === 0;
    updateVariants(currentVariants);
    setIsLoading(false);
    if (hasConfirmedCurrentIdentity) {
      isAssignmentLoadingRef.current = false;
      setIsAssignmentLoading(false);
    } else {
      recoverCurrentIdentityRefresh(lastFetchedUserId === undefined ? 'logout' : 'login');
    }
  }, [recoverCurrentIdentityRefresh, updateVariants]);

  useEffect(() => {
    isMountedRef.current = true;

    if (hasCompletedExperimentsInitialization) {
      // A remount after startup already completed adopts the current assignment immediately.
      adoptCurrentIdentityAssignment();
      return () => {
        isMountedRef.current = false;
      };
    }

    const startupPromise = initializeExperiments();
    const generation = startupFetchGeneration;
    startupPromise
      .then(client => {
        if (generation === experimentFetchGeneration) {
          // Exposure reporting resumes only after the startup assignment is confirmed.
          isAssignmentLoadingRef.current = false;
          if (isMountedRef.current) {
            const confirmedVariants = client.all();
            updateVariants(confirmedVariants);
          }
        }
      })
      .catch(error => {
        logger.error({
          error,
          message: 'Amplitude experiments assignment refresh failure',
          operation: 'startup',
        });
        // Startup failed terminally. Retry the current identity once in the background so a
        // transient cold-start failure recovers without a remount or login, unless a newer identity
        // transition superseded startup or another refresh (e.g. an Identify) already confirmed it.
        void delay(STARTUP_BACKGROUND_RETRY_DELAY_MS).then(() => {
          if (
            isMountedRef.current &&
            experimentFetchGeneration === generation &&
            lastConfirmedFetchGeneration !== generation
          ) {
            recoverCurrentIdentityRefresh('startup');
          }
        });
      })
      .finally(() => {
        if (!isMountedRef.current) {
          return;
        }
        if (generation === experimentFetchGeneration) {
          setIsLoading(false);
          setIsAssignmentLoading(false);
          return;
        }
        // Startup was superseded by a newer identity change. If this instance still has no active
        // transition and never resumed exposure, that change ran on a detached previous instance and
        // this one would load indefinitely; adopt the current assignment and recover it. Otherwise
        // this instance's own transition publishes it, so leave that to settle.
        if (isAssignmentLoadingRef.current && activeIdentityRef.current === null) {
          adoptCurrentIdentityAssignment();
        }
      });

    return () => {
      isMountedRef.current = false;
    };
  }, [adoptCurrentIdentityAssignment, recoverCurrentIdentityRefresh, updateVariants]);

  useEffect(() => {
    syncDatadogExperimentVariantContext(variants);
  }, [variants]);

  const logExposureInternal = useCallback(
    (
      flagKey: string | RunningExperiments,
      type: 'Manual' | 'Auto' = 'Manual',
      options?: ExposureOptions,
      renderedExposureRevision: number = activeExposureRevisionRef.current
    ): boolean => {
      // Suppress exposures while an identity refresh is unresolved, and reject callbacks captured
      // for a superseded identity so a previous identity's assignment is never reported.
      if (
        isAssignmentLoadingRef.current ||
        renderedExposureRevision !== activeExposureRevisionRef.current
      ) {
        return false;
      }

      const committedVariant = variants[flagKey];
      if (!committedVariant?.value) {
        return false;
      }

      const exposureVariant = options?.useExperimentVariantKey
        ? committedVariant.key ?? committedVariant.value
        : committedVariant.value;
      const dedupeKey = options?.dedupeKey ?? '';
      const exposureDedupeKey = getExposureDedupeKey(flagKey, committedVariant, dedupeKey);
      if (flagExposuresHandled.current.has(exposureDedupeKey)) {
        return true;
      }

      const latestVariant = exposureVariantsRef.current[flagKey];
      const metadata = hasSameAssignmentIdentity(committedVariant, latestVariant)
        ? latestVariant?.metadata
        : committedVariant.metadata;

      logEvent('$exposure', EventTypes.Other, {
        experiment_key: committedVariant.expKey,
        flag_key: flagKey,
        metadata,
        variant: exposureVariant,
      });
      flagExposuresHandled.current.set(exposureDedupeKey, true);
      logger.info(
        `[Experiment] ${type} Logging exposure for "${flagKey}", variant: "${exposureVariant}"`
      );
      return true;
    },
    [variants]
  );

  const logExposure = useCallback(
    (flagKey: string | RunningExperiments, options?: ExposureOptions): boolean =>
      logExposureInternal(flagKey, 'Manual', options, exposureRevision),
    [exposureRevision, logExposureInternal]
  );

  const handleLogin = useCallback(
    (userId: string) => {
      runIdentityRefresh(userId, 'login');
    },
    [runIdentityRefresh]
  );

  const handleLogout = useCallback(() => {
    runIdentityRefresh(undefined, 'logout');
  }, [runIdentityRefresh]);

  useEffect(
    () =>
      // Consume Amplitude identity transitions so assignment state follows the current identity
      // even when the SDK, not an explicit auth handler, drives the change.
      subscribeToAmplitudeIdentityUpdates(notification => {
        if (notification.type === 'user-id') {
          runIdentityRefresh(notification.userId, notification.userId ? 'login' : 'logout');
          return;
        }
        // A successful Identify that completes while a login transition is still WAITING for it is
        // incorporated by that transition's fetch, so a separate refresh here would just duplicate
        // the evaluation. Only refresh when no transition is in flight (a property update outside a
        // login), or when a login already proceeded at its deadline with the Identify still pending
        // (its fetch used stale properties), so the late completion is not dropped.
        if (activeIdentityRef.current === null || loginNeedsLateIdentifyRefreshRef.current) {
          loginNeedsLateIdentifyRefreshRef.current = false;
          refreshCurrentIdentityProperties();
        }
      }),
    [refreshCurrentIdentityProperties, runIdentityRefresh]
  );

  const getVariantValue = useCallback((flagKey: string | RunningExperiments) => variants[flagKey], [
    variants,
  ]);

  const variant = useCallback(
    (flagKey: string, expectedVariant: string, options = { skipExposureLogging: false }) => {
      if (!options.skipExposureLogging) {
        logExposureInternal(flagKey, 'Auto', undefined, exposureRevision);
      }

      const foundVariant = variants[flagKey];
      return foundVariant?.value === expectedVariant;
    },
    [exposureRevision, logExposureInternal, variants]
  );

  const value: IExperimentsContext = {
    isLoading,
    isAssignmentLoading,
    assignmentLoadingDeadline,
    loadingCycle,
    exposureRevision,
    hasAssignmentFetchFailed,
    getVariantValue,
    logExposure,
    handleLogin,
    handleLogout,
    allVariants: variants,
    variant,
  };

  return <Context.Provider value={value}>{children}</Context.Provider>;
};
